import React from "react";
import { Link } from "react-router-dom";

import { faFile, faTriangleExclamation, faUpload } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonStandard, Text } from "@performyard/py-components";

import { Attachment } from "../../../components/Attachment";
import { MemoizedMarkdown } from "../../../components/Markdown";
import { CompetencyFrameworkCard } from "./CompetencyFrameworkCard";
import { FormQuestionSuggestionsContainer } from "./FormQuestionSuggestionsContainer";
import { FormSuggestionsContainer } from "./FormSuggestionsContainer";
import { GoalSuggestionsContainer } from "./GoalSuggestionsContainer";
import { ImprovementsContainer } from "./ImprovementsContainer";
import { SourcesBox } from "./SourcesBox";

import { useEditFormStore } from "../../../pages/Administration/Reviews/EditForm/useEditFormStore";

import type { ChatMessage as ChatMessageType } from "../types";

import { useFormCreation } from "../hooks/useFormCreation";
import { useGoalCreation } from "../hooks/useGoalCreation";

import { questionSuggestionToReviewQuestion } from "../utils/question-suggestion-to-review-question";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type ChatMessageProps = {
	message: ChatMessageType;
	onCompetencyUpload?: () => void;
	competencyUploadDisabled?: boolean;
};

const UploadPdfButton: React.FC<{ onClick: () => void; disabled?: boolean }> = ({ onClick, disabled }) => (
	<div className="flex flex-col items-start gap-1">
		<ButtonStandard
			variant="outline"
			color="primary"
			size="medium"
			iconLeft={faUpload}
			onClick={onClick}
			disabled={disabled}
			dangerouslySetClassName="!w-auto"
		>
			Upload a PDF
		</ButtonStandard>
		<Text size="caption" color="neutral-6">
			Up to 10MB
		</Text>
	</div>
);

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onCompetencyUpload, competencyUploadDisabled }) => {
	const openDocumentModal = useAskPerformYardStore((state) => state.openDocumentModal);
	const goalSuggestionStates = useAskPerformYardStore((state) => state.goalSuggestionStates);
	const creatingSuggestionIds = useAskPerformYardStore((state) => state.creatingSuggestionIds);
	const markGoalDeleted = useAskPerformYardStore((state) => state.markGoalDeleted);
	const openGoalEditPanel = useAskPerformYardStore((state) => state.openGoalEditPanel);
	const goalImprovementStates = useAskPerformYardStore((state) => state.goalImprovementStates);
	const applyImprovement = useAskPerformYardStore((state) => state.applyImprovement);
	const markImprovementDeleted = useAskPerformYardStore((state) => state.markImprovementDeleted);
	const formImprovementStates = useAskPerformYardStore((state) => state.formImprovementStates);
	const applyFormImprovement = useAskPerformYardStore((state) => state.applyFormImprovement);
	const markFormImprovementDeleted = useAskPerformYardStore((state) => state.markFormImprovementDeleted);
	const formSuggestionStates = useAskPerformYardStore((state) => state.formSuggestionStates);
	const creatingFormIds = useAskPerformYardStore((state) => state.creatingFormIds);
	const markFormDeleted = useAskPerformYardStore((state) => state.markFormDeleted);
	const formQuestionSuggestionStates = useAskPerformYardStore((state) => state.formQuestionSuggestionStates);
	const markFormQuestionAdded = useAskPerformYardStore((state) => state.markFormQuestionAdded);
	const markFormQuestionDeleted = useAskPerformYardStore((state) => state.markFormQuestionDeleted);
	const isExpanded = useAskPerformYardStore((state) => state.isExpanded);
	const toggleExpand = useAskPerformYardStore((state) => state.toggleExpand);
	const streamMessage = useAskPerformYardStore((state) => state.streamMessage);
	const reportBuilder = useAskPerformYardStore((state) => state.reportBuilder);
	const clearReportBuilder = useAskPerformYardStore((state) => state.clearReportBuilder);
	const addStaticAssistantMessage = useAskPerformYardStore((state) => state.addStaticAssistantMessage);
	const handleBuildAnotherReport = () => {
		clearReportBuilder();
		addStaticAssistantMessage("Ready for the next one — describe the report you'd like to build.");
	};
	const messageOwnsReportBuilder = reportBuilder?.messageId === message.id;
	const showReportCta =
		messageOwnsReportBuilder &&
		reportBuilder?.savedReportId != null &&
		!reportBuilder.savedReportId.startsWith("pr2-stub-");
	const showReportSavingIndicator = messageOwnsReportBuilder && reportBuilder?.status === "saving";
	const showReportSaveError = messageOwnsReportBuilder && reportBuilder?.status === "error";
	const { handleCreateGoalById } = useGoalCreation();
	const { createForm } = useFormCreation();
	const isAssistant = message.role === "assistant";
	const isUser = message.role === "user";

	const sources = message.sources ?? [];
	// Show sources if they exist (backend handles filtering based on confidence)
	const shouldShowSources = sources.length > 0;
	const hasSingleSource = shouldShowSources && sources.length === 1;
	const hasMultipleSources = shouldShowSources && sources.length >= 2;

	return (
		<div
			className={`box-border flex animate-in duration-200 fade-in slide-in-from-bottom-2 ${
				isUser ? "ml-auto w-[92%] flex-col items-end py-2" : "w-full flex-col items-start py-2"
			}`}
		>
			{isAssistant && (
				<div className="flex max-w-full items-end">
					{/* Avatar */}
					<img src="/static/img/py-logo/py-round-icon.svg" alt="PerformYard" className="h-6 w-6 shrink-0" />

					{/* Message content */}
					<div className="box-border flex min-w-0 flex-col gap-2 pl-2">
						<div className="overflow-hidden rounded-lg rounded-bl-none bg-neutral-1 px-2 py-2">
							{message.isImportProgress && message.isStreaming ? (
								<p className="animate-pulse text-sm text-neutral-600">{message.content}</p>
							) : message.isStreaming && !message.content && !message.competencySuggestions?.length ? (
								<p className="animate-pulse text-sm text-neutral-600">
									{streamMessage ?? "Thinking"}...
								</p>
							) : (
								<div className="break-words" aria-live={message.isStreaming ? "polite" : "off"}>
									<MemoizedMarkdown content={message.content} id={message.id} />
									{message.isStreaming && (
										<span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-neutral-400 align-middle" />
									)}
								</div>
							)}

							{/* Single source - inline display */}
							{hasSingleSource && (
								<div className="mt-3">
									<button
										type="button"
										onClick={() => openDocumentModal(sources[0].s3FileId, sources[0].filename)}
										className="inline-flex max-w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left text-sm text-primary-5 hover:!text-primary-7 hover:underline"
									>
										<FontAwesomeIcon icon={faFile} className="h-4 w-4 shrink-0" />
										<span className="truncate font-medium">{sources[0].filename}</span>
									</button>
								</div>
							)}

							{/* Multiple sources - collapsible box */}
							{hasMultipleSources && <SourcesBox sources={sources} onSourceClick={openDocumentModal} />}

							{/* Subtle indicator when response may be incomplete */}
							{message.isIncomplete && (
								<div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
									<FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3 shrink-0" />
									<span>This response may be incomplete</span>
								</div>
							)}
						</div>

						{/* Goal suggestions */}
						{message.goalSuggestions && message.goalSuggestions.length > 0 && (
							<GoalSuggestionsContainer
								suggestionStates={goalSuggestionStates.filter((gs) =>
									message.goalSuggestions!.some((s) => s.id === gs.suggestion.id)
								)}
								onCreateGoal={handleCreateGoalById}
								onDeleteGoal={(suggestionId) => markGoalDeleted(suggestionId)}
								onEditGoals={() => {
									if (!isExpanded) {
										toggleExpand();
									}
									openGoalEditPanel(message.id);
								}}
								creatingSuggestionIds={creatingSuggestionIds}
							/>
						)}

						{/* Goal quality coach improvements */}
						{message.goalImprovements && message.goalImprovements.length > 0 && (
							<ImprovementsContainer
								suggestionStates={goalImprovementStates.filter((gs) =>
									message.goalImprovements!.some((s) => s.id === gs.suggestion.id)
								)}
								onApply={applyImprovement}
								onDelete={markImprovementDeleted}
							/>
						)}

						{/* Form building coach improvements */}
						{message.formImprovements && message.formImprovements.length > 0 && (
							<ImprovementsContainer
								suggestionStates={formImprovementStates.filter((fs) =>
									message.formImprovements!.some((s) => s.id === fs.suggestion.id)
								)}
								onApply={applyFormImprovement}
								onDelete={markFormImprovementDeleted}
							/>
						)}

						{/* Form suggestions */}
						{message.formSuggestions && message.formSuggestions.length > 0 && (
							<FormSuggestionsContainer
								suggestionStates={formSuggestionStates.filter((fs) =>
									message.formSuggestions!.some((s) => s.id === fs.suggestion.id)
								)}
								onCreateForm={createForm}
								onDeleteForm={markFormDeleted}
								creatingFormIds={creatingFormIds}
							/>
						)}

						{/* Form question suggestions */}
						{message.formQuestionSuggestions && message.formQuestionSuggestions.length > 0 && (
							<FormQuestionSuggestionsContainer
								suggestionStates={formQuestionSuggestionStates.filter((fs) =>
									message.formQuestionSuggestions!.some((s) => s.id === fs.suggestion.id)
								)}
								onAddQuestion={(suggestion) => {
									// Guard against double-add: rapid clicks (or "Add All" + a card click)
									// can fire twice before re-render flips the button. Read fresh state
									// from the store synchronously and bail if already added.
									const fresh = useAskPerformYardStore
										.getState()
										.formQuestionSuggestionStates.find((s) => s.suggestion.id === suggestion.id);
									if (fresh?.status === "added") return;
									const reviewQuestion = questionSuggestionToReviewQuestion(suggestion);
									useEditFormStore.getState().addQuestion(reviewQuestion);
									markFormQuestionAdded(suggestion.id);
								}}
								onDeleteQuestion={markFormQuestionDeleted}
							/>
						)}
						{!message.isStreaming &&
							message.competencySuggestions &&
							message.competencySuggestions.length > 0 && (
								<CompetencyFrameworkCard messageId={message.id} />
							)}
						{showReportSavingIndicator && (
							<div className="inline-flex items-center text-sm text-neutral-600">
								<span className="animate-pulse">Saving your report…</span>
							</div>
						)}
						{showReportSaveError && (
							<div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
								<FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
								<div className="flex flex-col gap-1">
									<span className="font-medium">Couldn&apos;t save your report</span>
									<span className="text-xs text-red-600">
										{reportBuilder?.errorMessage ?? "Something went wrong. Please try again."}
									</span>
								</div>
							</div>
						)}
						{showReportCta && (
							<div className="flex flex-col gap-2">
								<Link
									to={`/analytics/report/${reportBuilder!.savedReportId}`}
									className="inline-flex items-center text-sm text-primary-5 hover:!text-primary-7 hover:underline"
								>
									Your report is ready — Open in Reporting &amp; Analytics →
								</Link>
								<button
									type="button"
									onClick={handleBuildAnotherReport}
									className="cursor-pointer self-start border-none bg-transparent p-0 text-xs text-neutral-600 hover:underline"
								>
									Build another report
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			{isAssistant && message.showCompetencyUpload && onCompetencyUpload && (
				<div className="pt-2 pl-8">
					<UploadPdfButton onClick={onCompetencyUpload} disabled={competencyUploadDisabled} />
				</div>
			)}

			{isUser &&
				(message.attachment ? (
					<Attachment {...message.attachment} dangerouslySetClassName="rounded-br-none w-[92%]" />
				) : (
					<div className="rounded-lg rounded-br-none bg-primary-5 px-2 py-2">
						<p className="text-sm leading-[16px] break-words whitespace-pre-wrap text-white">
							{message.content}
						</p>
					</div>
				))}
		</div>
	);
};

export default ChatMessage;
